import {
  Brush,
  Check,
  Close,
  DoNotDisturb,
  Landscape,
  Save
} from '@mui/icons-material'
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Tooltip,
  Typography
} from '@mui/material'
import React from 'react'
import { connect } from 'react-redux'
import { useParams } from 'react-router-dom'
import AvatarWithLoader from 'components/avatar-with-loader'
import { NivosCommentSection } from 'components/index'
import NivosRTE from 'components/nivos-rte'
import {
  Operations as LayoutOperations,
  operations as layoutOperations
} from 'states/dux/layout/operations'
import { Payload as LayoutPayload } from 'states/dux/layout/types'
import {
  Operations as NivosPostOperations,
  operations as nivosPostOperations
} from 'states/dux/nivos-post/operations'
import { Payload as NivosPostPayload } from 'states/dux/nivos-post/types'
import {
  Operations as ThemeOperations,
  operations as themeOperations
} from 'states/dux/theme/operations'
import { Payload as ThemePayload } from 'states/dux/theme/types'
import {
  Operations as UserOperations,
  operations as userOperations
} from 'states/dux/user/operations'
import { Payload as UserPayload } from 'states/dux/user/types'
import State from 'states/types'
import NivosPost from 'types/nivos-post'
import {
  get,
  mapOperationsToDispatchProps,
  objectifyDateStringFields,
  post as postFn
} from 'utils/common'

interface StateProps {
  theme: ThemePayload
  layout: LayoutPayload
  user: UserPayload
  nivosPost: NivosPostPayload
}

interface DispatchProps {
  operations: {
    theme: ThemeOperations
    layout: LayoutOperations
    user: UserOperations
    nivosPost: NivosPostOperations
  }
}

type AppProps = StateProps & DispatchProps

const PostDetailPageFC: React.FC<any> = (props: AppProps) => {
  const { operations, nivosPost, user } = props
  const [post, setPost] = React.useState<NivosPost>()
  const [rte, setRte] = React.useState('')
  const [plainText, setPlainText] = React.useState('')
  const [reStater, setReStater] = React.useState('')
  const [rteTemp, setRteTemp] = React.useState('')
  const [editMode, setEditMode] = React.useState(false)
  const [editLoading, setEditLoading] = React.useState(false)
  const [saved, setSaved] = React.useState(false)

  const routeParams = useParams()

  const setPostFromParams = (): void => {
    const assignOutright = (selectedPost: NivosPost): void => {
      setPost(selectedPost)
      setRte(selectedPost.contentRte)
      setPlainText(selectedPost.content)
    }

    if (routeParams?.id != null) {
      const postID = Number(routeParams?.id)
      const selectedPost = nivosPost.data.find(i => i.id === postID)

      if (selectedPost !== undefined) {
        if (post === undefined) {
          assignOutright(selectedPost)
        } else if (post.id === selectedPost.id) {
          const newState = { ...post }
          newState.contentRte = selectedPost.contentRte
          newState.content = selectedPost.content
          newState.comments = selectedPost.comments
          newState.title = selectedPost.title
          setPost(newState)
        } else {
          assignOutright(selectedPost)
        }
        setSaved(selectedPost.saved)
      }
    }
  }

  const saveRteChanges = (): void => {
    if (post === undefined) return

    const formData = new FormData()
    setEditLoading(true)

    formData.append('content', plainText)
    formData.append('content_rte', rte)

    void postFn(`/post/${post.id}/`, formData, true, false, 'PATCH').then(
      data => {
        operations.nivosPost.update(objectifyDateStringFields(data))
        setReStater('')
        setRteTemp('')
        setEditMode(false)
        setEditLoading(false)
        operations.nivosPost.loading(false)
      }
    )
  }

  const savePost = (): void => {
    if (post === undefined) return

    const formData = new FormData()

    formData.append('saved', 'True')

    void postFn(`/post/${post.id}/`, formData, true, false, 'PATCH').then(
      data => {
        operations.nivosPost.update(objectifyDateStringFields(data))
        operations.nivosPost.loading(false)
        setSaved(true)
      }
    )
  }

  React.useEffect(() => {
    // Fetch nivos posts
    if (nivosPost.data.length < 1) {
      operations.nivosPost.loading(true)
      void get('/post/').then(data => {
        const parsedData = data.map((item: any) =>
          objectifyDateStringFields(item)
        )
        operations.nivosPost.set(parsedData)
        operations.nivosPost.loading(false)
      })
    }

    window.scroll(0, 0)
  }, [])

  React.useEffect(() => setPostFromParams(), [nivosPost])

  return (
    <Box
      display={'flex'}
      flexDirection={'column'}
      width={'1'}
      sx={{ minHeight: 'calc(100vh - 80px)' }}
      paddingX={7}
      paddingY={8}
    >
      {post !== undefined && (
        <Box>
          <Box display={'flex'} justifyContent={'center'} alignItems={'center'}>
            <Box flexGrow={1}>
              <Typography fontSize={34} gutterBottom>
                {post.title.toUpperCase()}
              </Typography>
              <Typography variant={'body2'}>
                <strong>By:</strong> {post.createdBy.fullName},{' '}
                {post.createdAt.toUTCString().slice(4, 16)}
              </Typography>
            </Box>
            {post.createdBy.id === user.id && (
              <Tooltip title={editMode ? 'Save Changes' : 'Edit Post'}>
                <Button
                  sx={{ marginRight: 2 }}
                  startIcon={
                    !editLoading ? !editMode ? <Brush /> : <Save /> : undefined
                  }
                  variant={'outlined'}
                  onClick={() => {
                    if (!editMode) {
                      setReStater('')
                      setRteTemp(rte)
                      setEditMode(true)
                    } else {
                      saveRteChanges()
                    }
                  }}
                >
                  {!editLoading ? (
                    !editMode ? (
                      'Edit Post'
                    ) : (
                      'Save Changes'
                    )
                  ) : (
                    <CircularProgress size={28} />
                  )}
                </Button>
              </Tooltip>
            )}
            {editMode && (
              <Tooltip title={'Discard Changes'}>
                <Button
                  variant={'outlined'}
                  startIcon={<DoNotDisturb />}
                  onClick={() => {
                    if (rteTemp.trim() !== '') {
                      setReStater(rteTemp)
                      setRteTemp('')
                    }
                    setEditMode(false)
                  }}
                >
                  Discard
                </Button>
              </Tooltip>
            )}
            {!editMode && (
              <Tooltip title={'Close'}>
                <IconButton
                  size={'large'}
                  sx={{ fontSize: 48, height: 'fit-content' }}
                  onClick={() => window.history.back()}
                >
                  <Close fontSize={'large'} />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          <Box paddingY={3} />
          <Divider />
          <Box paddingY={3} />

          <Box>
            {post.images !== undefined && post.images.length > 0 && (
              <AvatarWithLoader
                src={post.images[0].url}
                avatarProps={{
                  sx: {
                    width: 1,
                    height: 500,
                    borderRadius: 4
                  }
                }}
                LoaderIcon={Landscape}
              />
            )}
          </Box>

          <Box paddingY={3} />

          <Box>
            <NivosRTE
              rteState={rte}
              reStater={reStater}
              setRteState={setRte}
              setPLainState={setPlainText}
              readOnly={!editMode}
            />
          </Box>

          <Box paddingY={3} />

          <Box border={2} padding={3} borderRadius={4} borderColor={'#d4d2d2'}>
            <NivosCommentSection
              comments={post.comments}
              submitFnCallback={comment => {
                void postFn(`/post/${post.id}/add_comment/`, {
                  comment
                }).then(data => {
                  const newPost = objectifyDateStringFields(data)
                  operations.nivosPost.update(newPost)
                })
              }}
            />
          </Box>

          <Box paddingY={3} />

          <Box display={'flex'}>
            <Box flexGrow={1} />
            <Box padding={2}>
              <Button
                size={'large'}
                variant={'outlined'}
                disabled={saved}
                startIcon={saved ? <Check /> : <Save fontSize={'large'} />}
                onClick={() => savePost()}
              >
                Save Post
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  )
}

const mapStateToProps = (state: State): StateProps => {
  return {
    theme: state.theme,
    layout: state.layout,
    user: state.user,
    nivosPost: state.nivosPost
  }
}

const mapDispatchToProps = mapOperationsToDispatchProps({
  theme: themeOperations,
  layout: layoutOperations,
  user: userOperations,
  nivosPost: nivosPostOperations
})

const PostDetailPage = connect(
  mapStateToProps,
  mapDispatchToProps
)(PostDetailPageFC) as React.FC

export default PostDetailPage
